import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { fmtEuro, fmtNum, fmtPct } from "../../lib/format";
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react";

const statoStyle = {
  ok: { bg: "bg-[#EEF4EB]", text: "text-[#4F683E]", border: "border-[#9CAF88]", Icon: CheckCircle2 },
  warn: { bg: "bg-[#FEF9E7]", text: "text-[#8C7B2E]", border: "border-[#F7E8A4]", Icon: AlertCircle },
  neg: { bg: "bg-[#FDF2F4]", text: "text-[#9E4756]", border: "border-[#F8D7DA]", Icon: XCircle },
};

const livelloStyle = {
  Alto: "bg-[#EEF4EB] text-[#4F683E] border-[#9CAF88]",
  Medio: "bg-[#FEF9E7] text-[#8C7B2E] border-[#F7E8A4]",
  "Da approfondire": "bg-[#FDF2F4] text-[#9E4756] border-[#F8D7DA]",
};

export const Results = ({ result }) => {
  const chartData = result.anni.map((a) => ({ anno: String(a.anno), credito: Number(a.credito.toFixed(2)) }));

  return (
    <div className="animate-rise space-y-8" data-testid="results-page">
      <div>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#2B3A42]">
          Stima prudenziale del credito d'imposta
        </h2>
      </div>

      {/* Highlight box */}
      <div data-testid="total-credit-highlight-box"
        className="rounded-2xl bg-[#EEF4EB] border border-[#9CAF88] p-6 sm:p-8 text-center">
        <div className="text-sm uppercase tracking-[0.15em] text-[#5B6E78] font-medium">
          Credito d'imposta totale stimato
        </div>
        <div className="mt-2 text-4xl sm:text-5xl font-semibold text-[#4F683E] tnum">
          {fmtEuro(result.credito_totale)}
        </div>
      </div>

      {/* Table 1 */}
      <div className="rounded-2xl bg-white border border-[#DDE5E2] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-annual-breakdown">
            <thead>
              <tr className="bg-[#6C8EAD] text-white text-left">
                <th className="px-4 py-3 font-medium">Anno</th>
                <th className="px-4 py-3 font-medium text-right">Costo del personale</th>
                <th className="px-4 py-3 font-medium text-center">Persone / Dipendenti</th>
                <th className="px-4 py-3 font-medium text-center">Aliquota</th>
                <th className="px-4 py-3 font-medium text-right">Credito stimato</th>
                <th className="px-4 py-3 font-medium text-right">Incidenza sul fatturato</th>
              </tr>
            </thead>
            <tbody>
              {result.anni.map((a) => (
                <tr key={a.anno} className="border-t border-[#DDE5E2]">
                  <td className="px-4 py-3 font-medium">{a.anno}</td>
                  <td className="px-4 py-3 text-right tnum">{fmtEuro(a.costo_personale)}</td>
                  <td className="px-4 py-3 text-center tnum">{a.persone_progetto} / {a.dipendenti}</td>
                  <td className="px-4 py-3 text-center tnum">{fmtNum(a.aliquota * 100, 0)}%</td>
                  <td className="px-4 py-3 text-right tnum font-medium">{fmtEuro(a.credito)}</td>
                  <td className="px-4 py-3 text-right tnum">{fmtPct(a.incidenza, 2)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#9CAF88] bg-[#EEF4EB] font-semibold">
                <td className="px-4 py-3">Totale</td>
                <td></td><td></td><td></td>
                <td className="px-4 py-3 text-right tnum">{fmtEuro(result.credito_totale)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bar chart */}
      <div className="rounded-2xl bg-white border border-[#DDE5E2] p-4 sm:p-6" data-testid="chart-credit-by-year">
        <h3 className="text-lg font-medium text-[#2B3A42] mb-4">Credito stimato per anno</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F0" vertical={false} />
            <XAxis dataKey="anno" tick={{ fill: "#5B6E78", fontSize: 13 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#5B6E78", fontSize: 12 }} axisLine={false} tickLine={false}
              tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => [fmtEuro(v), "Credito"]} cursor={{ fill: "#EDF2F0" }} />
            <Bar dataKey="credito" radius={[8, 8, 0, 0]} maxBarSize={80}>
              {chartData.map((_, i) => <Cell key={i} fill={i % 2 === 0 ? "#6C8EAD" : "#9CAF88"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table 2 - altri costi */}
      {result.mostra_altri_costi && (
        <div className="rounded-2xl bg-white border border-[#DDE5E2] overflow-hidden" data-testid="table-other-detected-costs">
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-lg font-medium text-[#2B3A42]">Altri costi rilevati (non inclusi nella stima)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#EDF2F0] text-[#2B3A42] text-left">
                  <th className="px-4 py-3 font-medium">Anno</th>
                  <th className="px-4 py-3 font-medium text-right">Costo amministratore</th>
                  <th className="px-4 py-3 font-medium text-right">Professionisti esterni</th>
                  <th className="px-4 py-3 font-medium text-right">Totale</th>
                </tr>
              </thead>
              <tbody>
                {result.altri_costi.map((a) => (
                  <tr key={a.anno} className="border-t border-[#DDE5E2]">
                    <td className="px-4 py-3 font-medium">{a.anno}</td>
                    <td className="px-4 py-3 text-right tnum">{fmtEuro(a.costo_amministratore)}</td>
                    <td className="px-4 py-3 text-right tnum">{fmtEuro(a.costi_professionisti)}</td>
                    <td className="px-4 py-3 text-right tnum">{fmtEuro(a.totale)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-[#F7E8A4] bg-[#FEF9E7] font-semibold">
                  <td className="px-4 py-3">Totale</td>
                  <td className="px-4 py-3 text-right tnum">{fmtEuro(result.altri_costi_admin_totale)}</td>
                  <td className="px-4 py-3 text-right tnum">{fmtEuro(result.altri_costi_prof_totale)}</td>
                  <td className="px-4 py-3 text-right tnum">{fmtEuro(result.altri_costi_totale)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="px-4 py-3 text-xs text-[#5B6E78]">
            Questi costi non sono stati considerati nella stima. La loro eventuale ammissibilità verrà valutata nell'analisi approfondita.
          </p>
        </div>
      )}

      <p className="text-sm text-[#5B6E78] italic border-l-2 border-[#9CAF88] pl-4">
        La stima ipotizza che le attività rientrino nell'innovazione tecnologica finalizzata a obiettivi
        di transizione ecologica o innovazione digitale 4.0.
      </p>

      {/* Griglia di verifica */}
      <div>
        <h3 className="text-lg font-medium text-[#2B3A42] mb-4">Griglia di verifica preliminare</h3>
        <div className="space-y-2.5" data-testid="preliminary-verification-grid">
          {result.griglia.map((g) => {
            const s = statoStyle[g.stato];
            const Icon = s.Icon;
            return (
              <div key={g.key} className={`flex items-start gap-3 rounded-xl border ${s.border} ${s.bg} px-4 py-3`}>
                <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${s.text}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#2B3A42]">{g.label}</div>
                  <div className={`text-sm ${s.text}`}>{g.testo}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Livello preparazione */}
      <div data-testid="readiness-level-indicator"
        className={`rounded-2xl border p-5 ${livelloStyle[result.livello_preparazione]}`}>
        <div className="text-xs uppercase tracking-[0.15em] font-medium opacity-80">Livello di preparazione</div>
        <div className="text-2xl font-semibold mt-1">{result.livello_preparazione}</div>
        <div className="text-xs mt-2 opacity-80">Indicatore preliminare basato esclusivamente sui dati dichiarati.</div>
      </div>

      {/* Final message */}
      <div className="rounded-2xl bg-[#E9F0F5] border border-[#6C8EAD]/30 p-6 text-center">
        <p className="text-[#2B3A42] font-medium">
          Grazie, la sua simulazione è stata registrata.
        </p>
        <p className="text-[#5B6E78] text-sm mt-1">
          Lo studio potrà inviarle il report all'indirizzo email indicato.
        </p>
      </div>
    </div>
  );
};
